import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

import Button from '../common/Button';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../config/supabase';

interface Profile {
  display_name: string;
}

const ProfilePage: React.FC = () => {
  const { user } = useAuthStore();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', user.id)
          .single();

        if (fetchError) throw fetchError;
        setProfile(data);
        setDisplayName(data.display_name);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      }
    };

    fetchProfile();
  }, [user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ display_name: displayName })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess('Profile updated successfully');
      setProfile(prev => ({ ...prev!, display_name: displayName }));
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
        <div className="max-w-3xl mx-auto">
          <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
            <div className="text-center py-8">
              <p className="text-ink/70 dark:text-parchment/60">
                Please log in to view your profile.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment">
            Your Profile
          </h1>
        </div>

        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 space-y-8">
          {error && (
            <div className="bg-sun/10 text-sun p-3 rounded-md">
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-moon/10 text-moon p-3 rounded-md">
              {success}
            </div>
          )}

          <div>
            <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
              Profile Information
            </h2>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={user.email}
                  disabled
                  className="w-full px-3 py-2 border border-mist dark:border-dusk rounded-lg bg-parchment/60 dark:bg-shadow/60 text-ink/70 dark:text-parchment/60"
                />
              </div>

              <div>
                <label htmlFor="displayName" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                >
                  Update Profile
                </Button>
              </div>
            </form>
          </div>

          <div className="border-t border-mist dark:border-dusk pt-8">
            <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
              Change Password
            </h2>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60"
                  minLength={8}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60"
                  minLength={8}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                >
                  Update Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePage;
